import type { Configuration } from "@azure/msal-browser";
import { LogLevel, PublicClientApplication } from "@azure/msal-browser";

import { api } from "@/api";
import { AppConfig } from "@/api/config";
import { paths } from "@/api/url";

import type { AuthConfigParams, BootConfig } from "./types";

let _msalInstance: PublicClientApplication | null = null;
let _loginRequest: { scopes: string[] } | null = null;
let _encryptionKey: string | null = null;

export async function initAuth(params?: AuthConfigParams): Promise<void> {
  // ✅ Prevent accidental re-initialization
  if (_msalInstance) {
    return;
  }

  const apiBase = AppConfig.apiBaseUrl;
  const urlForErrorContext = `${apiBase}${paths.v1.authConfig()}`;

  let cfg: BootConfig;

  try {
    cfg = await api.AuthConfig(params);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to load auth config from ${urlForErrorContext}: ${msg}`);
  }

  if (!cfg || typeof cfg !== "object") {
    throw new Error("Auth config payload is empty or invalid.");
  }
  if (!cfg.publicMsalConfig?.auth?.clientId) {
    throw new Error("Auth config is missing publicMsalConfig.auth.clientId.");
  }
  if (!Array.isArray(cfg.loginRequest?.scopes)) {
    throw new Error("Auth config is missing loginRequest.scopes (array).");
  }

  _loginRequest = cfg.loginRequest;

  const redirectUri = cfg.publicMsalConfig.auth.redirectUri
    ? new URL(cfg.publicMsalConfig.auth.redirectUri, window.location.origin).toString()
    : window.location.origin;

  const msalConfig: Configuration = {
    auth: {
      ...cfg.publicMsalConfig.auth,
      redirectUri,
      postLogoutRedirectUri:
        cfg.publicMsalConfig.auth.postLogoutRedirectUri ?? `${window.location.origin}/login`,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
        loggerCallback: () => {
          /* noop */
        },
      },
    },
  };

  _msalInstance = new PublicClientApplication(msalConfig);
}

export function getMsalInstance(): PublicClientApplication {
  if (!_msalInstance) {
    throw new Error("MSAL not initialized. Call initAuth() first.");
  }
  return _msalInstance;
}

export function getLoginRequest(): { scopes: string[] } {
  if (!_loginRequest) {
    throw new Error("Login request not loaded. Call initAuth() first.");
  }
  return _loginRequest;
}


export function getEncryptionKey(): string {
  if (!_encryptionKey) {
    throw new Error("Encryption key not loaded. Call initAuth() first.");
  }

  return _encryptionKey;
}

export function setEncryptionKey(key: string): void {
  _encryptionKey = key;
}